import { SyntheticEvent, useEffect, useState } from 'react';
import InfiniteScroll from 'react-infinite-scroll-component';

import { useCreation, useReactive, useUpdateEffect } from 'ahooks';
import { Avatar, Button, Col, Divider, Input, List, Modal, Row, message } from 'antd';
import { isEmpty } from 'lodash';
import { observer } from 'mobx-react';
import { TBrigade } from 'types/brigades';

import { BrigadeListResp } from '@api/responseModels/brigades/brigadeListResponse';
import { CRMAPIManager } from '@classes/crmApiManager';
import { messageService } from '@classes/messageService';
import { SettingsManager } from '@classes/settingsManager';
import { faCheck } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { store as pStore } from '@molecules/paginationControls/pageSizeStore';
import { imagePicker } from '@utils/imagePicker';

const { Search } = Input;

export const ParentBrigadePicker = observer(
    ({
        isModalOpen,
        setIsModalOpen,
        currentBrigade,
        setCurrentBrigade,
        title = 'Выберите родительскую бригаду',
    }) => {
        const state = useReactive({
            isBrigadeListLoading: false,
            currentPage: 1,
            currentMeta: null,
        });

        const [brigadeList, setBrigadeList] = useState([]);
        const [selectedBrigade, setSelectedBrigade] = useState<TBrigade>();
        const [dataSource, setDataSource] = useState([]);

        const creds = useCreation(() => SettingsManager.getConnectionCredentials(), []);

        async function getBrigadeList(page: number = state.currentPage) {
            if (state.isBrigadeListLoading) return;
            state.isBrigadeListLoading = true;
            try {
                const brigadeListResp = await CRMAPIManager.request<BrigadeListResp>(
                    async (api) => {
                        const params = {
                            crm_id: creds.crmID,
                            page,
                            per_page: pStore.pS,
                        };

                        return await api.getBrigadeList(params);
                    }
                );
                if (brigadeListResp.errorMessages) {
                    setBrigadeList([]);
                    throw brigadeListResp.errorMessages;
                }

                setBrigadeList((prev) => {
                    return [...prev, ...brigadeListResp.data.data];
                });
                state.currentMeta = brigadeListResp.data.meta;
            } catch (err) {
                messageService.sendError(err.message);
            }
            state.isBrigadeListLoading = false;
        }

        async function handleChangePage() {
            getBrigadeList(state.currentPage + 1).then(() => {
                state.currentPage += 1;
            });
        }

        const handlePickerOK = () => {
            setCurrentBrigade(selectedBrigade);
            setIsModalOpen(false);
        };

        const handlePickerCancel = () => {
            setIsModalOpen(false);
        };

        function handleSelectButtonClick(e: SyntheticEvent, brigade: TBrigade) {
            e.stopPropagation();

            const isBrigadeAlreadyAdded = selectedBrigade?.id === brigade.id;
            if (isBrigadeAlreadyAdded) {
                setSelectedBrigade(null);
                return;
            }
            setSelectedBrigade(brigade);
            message.success(`Выбрана бригада ${brigade.name}`);
        }

        function beforeMountBrigadeList() {
            getBrigadeList();
        }

        useEffect(() => {
            beforeMountBrigadeList();
        }, []);

        useEffect(() => {
            if (state.currentMeta?.total == null || state.currentMeta?.total == 0) return;
            const newPage = Math.ceil(state.currentMeta.from / pStore.pS);
            getBrigadeList(newPage).then(() => {
                state.currentPage = newPage;
            });
        }, [pStore.pS]);

        useUpdateEffect(() => {
            if (isEmpty(brigadeList)) return;
            setDataSource(brigadeList);
        }, [brigadeList.length]);

        useEffect(() => {
            if (isEmpty(currentBrigade)) return;
            setSelectedBrigade(currentBrigade);
        }, [currentBrigade]);

        return (
            <Modal
                className="product-category-picker"
                title={title}
                open={isModalOpen}
                onCancel={handlePickerCancel}
                cancelText="Отмена"
                wrapClassName="sticky-modal"
                footer={[
                    <Button key="back" onClick={handlePickerCancel}>
                        Отмена
                    </Button>,
                    <Button key="submit" type="primary" onClick={handlePickerOK}>
                        Сохранить
                    </Button>,
                ]}
            >
                <Row>
                    <Col span={24}>
                        <Search allowClear className="header-search-input" onChange={() => {}} />
                    </Col>
                </Row>

                <InfiniteScroll
                    dataLength={dataSource.length}
                    next={handleChangePage}
                    hasMore={dataSource.length < state.currentMeta?.total}
                    loader={<Divider plain>Загрузка...</Divider>}
                    endMessage={<Divider plain>Все бригады загружены :^)</Divider>}
                    height={
                        window.screen.height < 950 ? 'calc(90vh - 220px)' : 'calc(75vh - 220px)'
                    }
                >
                    <List
                        className="customer-card-list"
                        dataSource={dataSource}
                        itemLayout="horizontal"
                        renderItem={(item) => (
                            <List.Item
                                className="customer-card"
                                key={item.id}
                                onClick={(e) => handleSelectButtonClick(e, item)}
                            >
                                <Row style={{ width: '100%' }}>
                                    <Col span={3}>
                                        <Avatar
                                            className="avatar"
                                            src={imagePicker({ picture: item?.picture })}
                                        />
                                    </Col>
                                    <Col span={18}>
                                        <Row>
                                            <Col>
                                                <h3 className="title">{item.name}</h3>
                                            </Col>
                                        </Row>
                                    </Col>
                                    <Col span={3} className="button-select">
                                        <Button
                                            icon={<FontAwesomeIcon icon={faCheck} />}
                                            shape="circle"
                                            type={
                                                item?.id === selectedBrigade?.id
                                                    ? 'primary'
                                                    : 'default'
                                            }
                                        />
                                    </Col>
                                </Row>
                            </List.Item>
                        )}
                    />
                </InfiniteScroll>
            </Modal>
        );
    }
);
